// useItemKindsStore — the item-kind taxonomy, cached for the session.
//
// WHY A STORE AND NOT A FETCH PER PAGE. `BOARD_CODES` used to be a frozen array
// in skuGrammar.ts, so every surface that offered a board type read the same
// constant for free. Now that the list is a table, the SKU builder (which mounts
// inside two different modals), the admin page and anything downstream would
// each fetch it. One cached list keeps the old ergonomics: ask for it, get it,
// and only the first asker pays.
//
// Precedent: stores/useAssetsStore.ts, which caches the asset-type registry for
// the same reason.
//
// ── THE SELECTOR TRAP ───────────────────────────────────────────────────────
// `useItemKindsStore((s) => s.structuredKinds())` is the obvious call and it is
// WRONG: the method returns a fresh array every time, Object.is never matches,
// and the component re-renders forever. So the filtering lives in exported pure
// functions over the array, and the store methods are thin wrappers for
// non-React callers (`useItemKindsStore.getState().byKey('PYR')`).
//
// In a component: subscribe to `kinds`, then useMemo one of the helpers on it.

import { create } from 'zustand';

export type CodeMode = 'structured' | 'short';

export interface ItemKind {
  /** The SKU segment (a board kind) or the stem of a short code. Never edited. */
  key: string;
  label: string;
  code_mode: CodeMode;
  tracked_per_instance_default: boolean;
  sort_order: number;
  is_active: boolean;
  /** Catalog rows carrying this kind. Absent on a create response, so 0. */
  item_count: number;
}

/** The slice of `useApi()` this store needs. Kept structural so a test can pass
 *  a plain object rather than standing up the hook. */
export interface KindsApi {
  get: (url: string, options?: object) => Promise<any>;
}

/** Server rows arrive over JSON, so booleans and numbers are coerced once here
 *  rather than defensively at every read site. */
export function normalizeKind(row: any): ItemKind {
  return {
    key: String(row?.key ?? ''),
    label: String(row?.label ?? ''),
    code_mode: row?.code_mode === 'short' ? 'short' : 'structured',
    tracked_per_instance_default: !!row?.tracked_per_instance_default,
    sort_order: Number(row?.sort_order ?? 0),
    is_active: row?.is_active !== false,
    item_count: Number(row?.item_count ?? 0),
  };
}

/** Sort order first, then the key, so two kinds sharing an order still land in a
 *  stable place instead of following insertion order. */
const byOrderThenKey = (a: ItemKind, b: ItemKind) =>
  a.sort_order - b.sort_order || a.key.localeCompare(b.key);

/** Board kinds, in the order the SKU builder should offer them. Active only:
 *  a retired kind stays on the items already wearing it and stops being a
 *  choice for new ones, exactly as a retired vendor type does. */
export function structuredKinds(kinds: ItemKind[]): ItemKind[] {
  return kinds.filter((k) => k.is_active && k.code_mode === 'structured').sort(byOrderThenKey);
}

/** Stems for everything that is not a board. */
export function shortKinds(kinds: ItemKind[]): ItemKind[] {
  return kinds.filter((k) => k.is_active && k.code_mode === 'short').sort(byOrderThenKey);
}

/** Retired kinds included: this answers "what is this segment", and the answer
 *  does not stop being true when the kind stops being offered. */
export function kindByKey(kinds: ItemKind[], key: string): ItemKind | undefined {
  const wanted = String(key || '').toUpperCase();
  return kinds.find((k) => k.key.toUpperCase() === wanted);
}

interface ItemKindsState {
  kinds: ItemKind[];
  loaded: boolean;
  loading: boolean;
  /** Why the list is missing, for a picker that has to say something. */
  error: string | null;
  /** Fetch once. `force` re-reads after an admin edit. */
  load: (api: KindsApi, force?: boolean) => Promise<void>;
  /** Replace the cache wholesale — the admin page's list IS this list. */
  setAll: (kinds: ItemKind[]) => void;
  /** Optimistic insert after an in-place create, so the picker that asked for
   *  the kind can select it without waiting for a round trip. */
  upsert: (kind: ItemKind) => void;
  structuredKinds: () => ItemKind[];
  shortKinds: () => ItemKind[];
  byKey: (key: string) => ItemKind | undefined;
}

// Two mounts of the SKU builder in one modal must not become two GETs, and a
// promise is the only thing that can be awaited by the second caller. Module
// scope rather than store state: it is never rendered.
let inflight: Promise<void> | null = null;

const useItemKindsStore = create<ItemKindsState>((set, get) => ({
  kinds: [],
  loaded: false,
  loading: false,
  error: null,

  load: async (api, force = false) => {
    if (get().loaded && !force) return;
    // A forced reload must not be swallowed by a request already in the air.
    if (inflight && !force) return inflight;
    set({ loading: true, error: null });
    inflight = (async () => {
      try {
        const res = await api.get('/item-kinds');
        const raw = Array.isArray(res) ? res : res?.kinds;
        set({ kinds: (raw || []).map(normalizeKind), loaded: true, error: null });
      } catch (e: any) {
        // Swallowed on purpose, and RECORDED. A picker that fails to load its
        // options is not a page-level failure — the surface still has a value to
        // show and a person to tell — but an unhandled rejection out of a mount
        // effect is noise nobody can act on.
        set({ error: e?.data?.error?.message || e?.message || 'Could not load item kinds' });
      } finally {
        set({ loading: false });
        inflight = null;
      }
    })();
    return inflight;
  },

  setAll: (kinds) => set({ kinds, loaded: true, error: null }),

  upsert: (kind) =>
    set((state) => {
      const next = normalizeKind(kind);
      const at = state.kinds.findIndex((k) => k.key === next.key);
      if (at === -1) return { kinds: [...state.kinds, next] };
      const kinds = state.kinds.slice();
      // An existing row keeps its item_count: a PATCH response carries the kind,
      // not the count of what wears it.
      kinds[at] = { ...next, item_count: next.item_count || kinds[at].item_count };
      return { kinds };
    }),

  structuredKinds: () => structuredKinds(get().kinds),
  shortKinds: () => shortKinds(get().kinds),
  byKey: (key) => kindByKey(get().kinds, key),
}));

export default useItemKindsStore;
